import React, { useState, useRef } from 'react';
import Modal from '../Modal';

const VITE_API_URL = import.meta.env.VITE_API_URL;

function MedicationPanel({
    // Drug Search Props
    searchQuery, handleSearch, searchResults, addDrugToPrescription,
    // List Props
    prescriptions, handlePrescriptionItemChange, removePrescriptionItem,
    // Interaction Props
    interactionWarnings, onCheckInteractions,
    // Medicine Suggestion Props
    medicineSuggestions, setMedicineSuggestions, 
    isSuggestionsLoading, onSuggestMedicines,
    // SIG Template Props
    sigTemplates, setIsSigModalOpen,
    // Modal Props
    isSigModalOpen, handleSaveTemplate, handleDeleteTemplate,
    editingSigTemplate, setEditingSigTemplate, newTemplate, setNewTemplate,
    // Auth
    authToken, patientAge, patientWeight, diagnosis
}) {
    // --- 1. STATES & REFS ---
    const [suggestionDrawerOpen, setSuggestionDrawerOpen] = useState(false);
    const [expandedSuggestions, setExpandedSuggestions] = useState({});
    const [dosageDrawer, setDosageDrawer] = useState({ 
        isOpen: false, medicine: null, suggestion: null, loading: false, error: null 
    });

    const searchInputRef = useRef(null);
    const aiCache = useRef({ medicines: {}, dosages: {} });

    // --- 2. HANDLERS ---
    const handleGetMedicineSuggestions = async () => {
        setSuggestionDrawerOpen(true);
        const cacheKey = `${diagnosis}-${patientAge}-${patientWeight}`;
        if (aiCache.current.medicines[cacheKey]) {
            setMedicineSuggestions(aiCache.current.medicines[cacheKey]);
            return;
        }
        const data = await onSuggestMedicines(); 
        if (data) aiCache.current.medicines[cacheKey] = data;
    };

    const handleSuggestDosage = async (medicine) => {
        const medName = medicine.trade_names || medicine.generic_name;
        const cacheKey = `${medName}-${diagnosis}-${patientAge}-${patientWeight}`;
        setDosageDrawer({ isOpen: true, medicine, suggestion: null, loading: true, error: null });
        if (aiCache.current.dosages[cacheKey]) {
            setDosageDrawer(prev => ({ ...prev, suggestion: aiCache.current.dosages[cacheKey], loading: false }));
            return;
        }
        try {
            const res = await fetch(`${VITE_API_URL}/ai/suggest-dosage`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ medicine, patient_age: patientAge, patient_weight: patientWeight, diagnosis })
            });
            if (res.ok) {
                const data = await res.json();
                aiCache.current.dosages[cacheKey] = data.suggestion;
                setDosageDrawer(prev => ({ ...prev, suggestion: data.suggestion, loading: false }));
            } else { setDosageDrawer(prev => ({ ...prev, error: 'Failed to get AI suggestion', loading: false })); }
        } catch (err) { setDosageDrawer(prev => ({ ...prev, error: err.message, loading: false })); }
    };

    const applyDosageSuggestion = () => {
        const { medicine, suggestion } = dosageDrawer;
        handlePrescriptionItemChange(medicine.tempId, 'quantity', suggestion.recommended_dose || '');
        handlePrescriptionItemChange(medicine.tempId, 'sig_instruction', suggestion.instructions || suggestion.frequency || '');
        handlePrescriptionItemChange(medicine.tempId, 'duration', suggestion.duration || '');
        setDosageDrawer(prev => ({ ...prev, isOpen: false }));
    };

    const handleAddSuggestedMedicine = (medicine) => {
        if (searchInputRef.current) {
            searchInputRef.current.value = medicine.generic_name;
            handleSearch({ target: { value: medicine.generic_name } });
            searchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const quickDurations = ['3 Days', '5 Days', '7 Days', '15 Days', '1 Month', 'Continue'];
    const quickTimings = ['After Meal', 'Before Meal', 'With Food', 'Empty Stomach', 'At Bedtime'];

    return (
        <fieldset className="p-5 border border-gray-200 rounded-xl mb-6 bg-white shadow-sm relative">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
                <legend className="text-lg font-bold text-indigo-800 flex items-center gap-2"><span>💊</span> Medications & Dosage</legend>
                <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded-full font-bold">{prescriptions.length} items</span>
            </div>

            {/* AI Suggest medicines */}
            <button onClick={handleGetMedicineSuggestions} disabled={isSuggestionsLoading} className="w-full mb-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-bold py-3 rounded-lg shadow-md transition-all flex items-center justify-center gap-2">
                {isSuggestionsLoading ? <span className="animate-spin">⏳</span> : <span>🤖</span>} Suggest Medicines (AI)
            </button>

            {/* Medicine Suggestion Drawer */}
            {suggestionDrawerOpen && (
                <div className="mb-6 border border-blue-200 rounded-lg overflow-hidden bg-white shadow-lg animate-fade-in">
                    {/* Header */}
                    <div className="bg-linear-to-r from-blue-600 to-cyan-600 text-white p-4 flex items-center justify-between">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <span>🤖</span> AI Suggestions
                            {medicineSuggestions.length > 0 && (
                                <span className="ml-auto bg-white text-blue-600 px-2 py-1 rounded-full text-xs font-bold">
                                    {medicineSuggestions.length}
                                </span>
                            )}
                        </h2>
                        <button
                            onClick={() => setSuggestionDrawerOpen(false)}
                            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="max-h-96 overflow-y-auto">
                        {isSuggestionsLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="text-center">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                                    <p className="text-gray-600 text-sm">Loading suggestions...</p>
                                </div>
                            </div>
                        ) : medicineSuggestions.length > 0 ? (
                            <div className="space-y-2 p-4">
                                {medicineSuggestions.map((medicine, idx) => (
                                    <div key={idx} className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50 hover:bg-blue-100 transition-colors">
                                        {/* Clickable Header */}
                                        <button
                                            type="button"
                                            onClick={() => setExpandedSuggestions(prev => ({
                                                ...prev,
                                                [idx]: !prev[idx]
                                            }))}
                                            className="w-full p-3 flex items-center justify-between text-left hover:bg-blue-100 transition-colors"
                                        >
                                            <div className="flex-1">
                                                <h4 className="font-bold text-gray-900 text-sm">
                                                    {medicine.trade_names || medicine.generic_name}
                                                </h4>
                                                <p className="text-xs text-gray-600 mt-1">
                                                    <span className="font-semibold">{medicine.generic_name}</span> • {medicine.strength}
                                                </p>
                                            </div>
                                            <svg 
                                                className={`w-5 h-5 text-blue-600 transition-transform shrink-0 ml-2 ${expandedSuggestions[idx] ? 'rotate-180' : ''}`}
                                                fill="none" 
                                                stroke="currentColor" 
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                            </svg>
                                        </button>

                                        {/* Expandable Details */}
                                        {expandedSuggestions[idx] && (
                                            <div className="border-t border-blue-200 p-3 bg-white space-y-3">
                                                {/* Dosage Info */}
                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                    <div className="bg-blue-50 p-2 rounded">
                                                        <p className="text-gray-600 font-semibold">Dose</p>
                                                        <p className="text-gray-900 font-bold">{medicine.recommended_dose}</p>
                                                    </div>
                                                    <div className="bg-blue-50 p-2 rounded">
                                                        <p className="text-gray-600 font-semibold">Frequency</p>
                                                        <p className="text-gray-900 font-bold">{medicine.frequency}</p>
                                                    </div>
                                                    <div className="bg-blue-50 p-2 rounded">
                                                        <p className="text-gray-600 font-semibold">Duration</p>
                                                        <p className="text-gray-900 font-bold">{medicine.duration}</p>
                                                    </div>
                                                    <div className="bg-blue-50 p-2 rounded">
                                                        <p className="text-gray-600 font-semibold">Instructions</p>
                                                        <p className="text-gray-900 font-bold">{medicine.instructions}</p>
                                                    </div>
                                                </div>

                                                {/* Rationale */}
                                                {medicine.rationale && (
                                                    <div className="bg-amber-50 border border-amber-200 rounded p-2">
                                                        <p className="text-xs font-semibold text-amber-900 mb-1">Why?</p>
                                                        <p className="text-xs text-amber-800">{medicine.rationale}</p>
                                                    </div>
                                                )}

                                                {/* Precautions */}
                                                {medicine.precautions && medicine.precautions.length > 0 && (
                                                    <div>
                                                        <p className="text-xs font-semibold text-red-700 mb-1">⚠️ Precautions</p>
                                                        <ul className="text-xs text-red-700 space-y-1">
                                                            {medicine.precautions.map((prec, pidx) => (
                                                                <li key={pidx} className="flex gap-1">
                                                                    <span>•</span>
                                                                    <span>{prec}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Add Button - Now populates search instead of directly adding */}
                                                <button
                                                    type="button"
                                                    onClick={() => handleAddSuggestedMedicine(medicine)}
                                                    className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded transition-colors flex items-center justify-center gap-1"
                                                >
                                                    <span>🔍</span> Find in Database
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-32">
                                <p className="text-center text-gray-600 text-sm p-4">
                                    No medicines suggested. Please add a diagnosis first.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}

                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <button
                            type="button"
                            onClick={() => setSuggestionDrawerOpen(false)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-medium transition-colors"
                        >
                            Close Suggestions
                        </button>
                    </div>
                </div>
            )}

            {/* Drug Search */}
            <div className="mb-6 relative z-30"> 
                <input ref={searchInputRef} className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" type="text" placeholder="Search Brand or Generic..." value={searchQuery} onChange={handleSearch} />
                <span className="absolute left-3 top-3.5 text-gray-400">🔍</span>
                {searchResults.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white border border-gray-200 rounded-lg max-h-60 overflow-y-auto shadow-2xl mt-1">
                        {searchResults.map(drug => (
                            <li key={drug.drug_id} className="p-3 border-b hover:bg-indigo-50 cursor-pointer flex justify-between items-center text-sm" onClick={() => addDrugToPrescription(drug)}>
                                <div><div className="font-bold">{drug.trade_names}</div><div className="text-xs text-gray-500">{drug.generic_name} - {drug.strength}</div></div>
                                <span className="text-[10px] text-gray-400">{drug.manufacturer}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Prescription List */}
            <div className="space-y-3 mb-6">
                {prescriptions.map((item, index) => (
                    <div key={item.tempId} className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:border-indigo-200">
                        <button onClick={() => removePrescriptionItem(item.tempId)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500">✕</button>
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="md:w-1/4">
                                <h4 className="font-bold text-gray-900 truncate">{item.trade_names || item.generic_name}</h4>
                                <p className="text-xs text-gray-500 truncate">{item.strength}</p>
                            </div>

                            <div className="md:w-3/4 grid grid-cols-12 gap-3">
                                {/* Qty */}
                                <div className="col-span-2">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase">Qty</label>
                                    <input className="w-full p-2 bg-gray-50 border rounded text-center text-sm" type="text" value={item.quantity} onChange={(e) => handlePrescriptionItemChange(item.tempId, 'quantity', e.target.value)} />
                                </div>

                                {/* Instructions (SIG) */}
                                <div className="col-span-5 relative group">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase">Instruction</label>
                                    <input 
                                        className="w-full p-2 bg-gray-50 border rounded text-sm focus:ring-2 focus:ring-indigo-500" 
                                        type="text" 
                                        list={`sig-list-${item.tempId}`} // Native Browser Autocomplete
                                        value={item.sig_instruction} 
                                        onChange={(e) => handlePrescriptionItemChange(item.tempId, 'sig_instruction', e.target.value)} 
                                    />
                                    
                                    {/* Native Dropdown for Templates */}
                                    <datalist id={`sig-list-${item.tempId}`}>
                                        {sigTemplates.map(t => <option key={t.template_id} value={t.instruction}>{t.title}</option>)}
                                    </datalist>

                                    {/* Custom Floating Menu for Timings & Saved Templates */}
                                    <div className="absolute top-full left-0 mt-1 bg-white border shadow-2xl rounded-lg p-3 z-20 hidden group-focus-within:block w-64 max-h-64 overflow-y-auto">
                                        {/* 1. Saved Templates Section */}
                                        {sigTemplates.length > 0 && (
                                            <div className="mb-3">
                                                <p className="text-[10px] font-bold text-indigo-500 uppercase mb-1 border-b pb-1">Saved Templates</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {sigTemplates.map(t => (
                                                        <button key={t.template_id} onMouseDown={(e) => { e.preventDefault(); handlePrescriptionItemChange(item.tempId, 'sig_instruction', t.instruction); }} className="text-[10px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1 rounded border border-indigo-100">
                                                            {t.title}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {/* 2. Quick Timings Section */}
                                        <p className="text-[10px] font-bold text-orange-500 uppercase mb-1 border-b pb-1">Add Timing</p>
                                        <div className="flex flex-wrap gap-1">
                                            {quickTimings.map(t => (
                                                <button key={t} onMouseDown={(e) => { e.preventDefault(); const current = item.sig_instruction; handlePrescriptionItemChange(item.tempId, 'sig_instruction', current ? `${current} ${t}` : t); }} className="text-[10px] bg-orange-50 hover:bg-orange-100 text-orange-700 px-2 py-1 rounded border border-orange-100">
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Duration */}
                                <div className="col-span-3 relative group">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase">Duration</label>
                                    <input className="w-full p-2 bg-gray-50 border rounded text-sm" type="text" value={item.duration} onChange={(e) => handlePrescriptionItemChange(item.tempId, 'duration', e.target.value)} />
                                    <div className="absolute top-full right-0 mt-1 bg-white border shadow-xl rounded-lg p-2 z-20 hidden group-focus-within:grid grid-cols-2 gap-1 w-32">
                                        {quickDurations.map(d => (
                                            <button key={d} onMouseDown={(e) => { e.preventDefault(); handlePrescriptionItemChange(item.tempId, 'duration', d); }} className="text-[10px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1.5 rounded">{d}</button>
                                        ))}
                                    </div>
                                </div>

                                <div className="col-span-2 flex items-end">
                                    <button type="button" onClick={() => handleSuggestDosage(item)} className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-[10px] font-bold">🤖 AI</button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Interaction Warnings */}
            {interactionWarnings.length > 0 && (
                <div className="mb-6 space-y-3">
                    {interactionWarnings.map((w, i) => {
                        // Determine color based on risk score or severity
                        const riskScore = w.risk_score || 7;
                        const severity = w.severity || 'moderate';
                        let borderColor = 'border-yellow-500';
                        let bgColor = 'bg-yellow-50';
                        let textColor = 'text-yellow-800';
                        let headerColor = 'text-yellow-700';
                        
                        if (severity === 'critical' || riskScore >= 9) {
                            borderColor = 'border-red-500';
                            bgColor = 'bg-red-50';
                            textColor = 'text-red-800';
                            headerColor = 'text-red-700';
                        } else if (severity === 'high' || riskScore >= 7) {
                            borderColor = 'border-orange-500';
                            bgColor = 'bg-orange-50';
                            textColor = 'text-orange-800';
                            headerColor = 'text-orange-700';
                        }

                        return (
                            <div key={i} className={`p-4 border-l-4 ${borderColor} ${bgColor} rounded-r-lg shadow-sm`}>
                                {/* Header with risk score */}
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className={`font-bold ${headerColor} flex items-center gap-2`}>
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        🤖 AI Drug Interaction Alert
                                    </h4>
                                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${borderColor === 'border-red-500' ? 'bg-red-200 text-red-900' : borderColor === 'border-orange-500' ? 'bg-orange-200 text-orange-900' : 'bg-yellow-200 text-yellow-900'}`}>
                                        Risk: {riskScore}/10 - {severity.toUpperCase()}
                                    </span>
                                </div>

                                {/* Drug names */}
                                <p className={`font-bold ${textColor} mb-2`}>
                                    {w.drug1 || w.drug1_name} + {w.drug2 || w.drug2_name}
                                </p>

                                {/* Mechanism of interaction */}
                                {w.mechanism && (
                                    <div className="mb-2">
                                        <p className={`text-xs font-semibold ${textColor} mb-1`}>How they interact:</p>
                                        <p className={`text-sm ${textColor} italic`}>{w.mechanism}</p>
                                    </div>
                                )}

                                {/* Clinical Impact */}
                                {w.clinical_impact && (
                                    <div className="mb-2">
                                        <p className={`text-xs font-semibold ${textColor} mb-1`}>Clinical Impact:</p>
                                        <p className={`text-sm ${textColor}`}>{w.clinical_impact}</p>
                                    </div>
                                )}

                                {/* Management */}
                                {w.management && (
                                    <div className="mb-2">
                                        <p className={`text-xs font-semibold ${textColor} mb-1`}>How to manage:</p>
                                        <p className={`text-sm ${textColor}`}>{w.management}</p>
                                    </div>
                                )}

                                {/* Alternative suggestions */}
                                {w.alternatives && w.alternatives.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-current border-opacity-20">
                                        <p className={`text-xs font-semibold ${textColor} mb-2`}>💡 Consider alternatives:</p>
                                        <div className="flex flex-wrap gap-2">
                                            {w.alternatives.map((alt, idx) => (
                                                <span key={idx} className={`text-xs px-2 py-1 rounded bg-white ${textColor} border ${borderColor}`}>
                                                    {alt}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="pt-4 border-t flex justify-between items-center">
                <button type="button" onClick={() => setIsSigModalOpen(true)} className="text-indigo-600 text-xs font-bold hover:underline">⚙️ MANAGE TEMPLATES</button>
                {prescriptions.length >= 2 && <button onClick={onCheckInteractions} className="text-orange-600 text-xs font-bold hover:underline uppercase">Check Interactions</button>}
            </div>

            {/* AI DOSAGE SIDE DRAWER */}
            {dosageDrawer.isOpen && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDosageDrawer(prev => ({ ...prev, isOpen: false }))}></div>
                    <div className="relative w-full max-w-sm bg-white h-full shadow-2xl flex flex-col animate-slide-in-right">
                        <div className="p-4 bg-purple-700 text-white flex justify-between items-center">
                            <h3 className="font-bold">AI Dosage Guide</h3>
                            <button onClick={() => setDosageDrawer(prev => ({ ...prev, isOpen: false }))} className="text-xl">✕</button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {dosageDrawer.loading ? <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full"></div></div> :
                             dosageDrawer.suggestion ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-purple-50 p-3 rounded">
                                            <p className="text-[10px] font-bold text-purple-600 uppercase">Dose</p>
                                            <p className="text-lg font-bold">{dosageDrawer.suggestion.recommended_dose}</p>
                                        </div>
                                        <div className="bg-purple-50 p-3 rounded">
                                            <p className="text-[10px] font-bold text-purple-600 uppercase">Freq</p>
                                            <p className="text-lg font-bold">{dosageDrawer.suggestion.frequency}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm border p-3 rounded bg-gray-50">{dosageDrawer.suggestion.instructions}</p>
                                    <p className="text-xs italic text-blue-700 bg-blue-50 p-3 rounded border border-blue-100">{dosageDrawer.suggestion.rationale}</p>
                                </div>
                            ) : null}
                        </div>
                        {!dosageDrawer.loading && (
                            <div className="p-4 bg-gray-50 border-t grid grid-cols-2 gap-3">
                                <button onClick={() => setDosageDrawer(prev => ({ ...prev, isOpen: false }))} className="py-2 border rounded font-bold">Cancel</button>
                                <button onClick={applyDosageSuggestion} className="py-2 bg-purple-700 text-white rounded font-bold">Apply</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Existing SIG Modal (Add/Edit Templates) */}
            <Modal isOpen={isSigModalOpen} onClose={() => { setIsSigModalOpen(false); setEditingSigTemplate(null); }} title="Manage Instruction Templates">
                {!editingSigTemplate ? (
                    <div className="space-y-4">
                        <div className="max-h-60 overflow-y-auto space-y-2">
                            {sigTemplates.map(t => (
                                <div key={t.template_id} className="flex justify-between p-3 border rounded items-center bg-gray-50">
                                    <div><div className="font-bold text-sm">{t.title}</div><div className="text-xs text-gray-500">{t.instruction}</div></div>
                                    <div className="flex gap-2">
                                        <button onClick={() => { setEditingSigTemplate(t); setNewTemplate(t); }} className="text-xs text-indigo-600">Edit</button>
                                        <button onClick={() => handleDeleteTemplate(t.template_id)} className="text-xs text-red-600">Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button onClick={() => { setEditingSigTemplate({ template_id: null }); setNewTemplate({ title: '', instruction: '' }); }} className="w-full py-2 bg-indigo-600 text-white rounded font-bold">+ Create New</button>
                    </div>
                ) : (
                    <form onSubmit={handleSaveTemplate} className="space-y-4">
                        <input className="w-full p-2 border rounded" placeholder="Title (e.g. TID)" value={newTemplate.title} onChange={e => setNewTemplate({...newTemplate, title: e.target.value})} required />
                        <input className="w-full p-2 border rounded" placeholder="Instruction (e.g. 1+1+1)" value={newTemplate.instruction} onChange={e => setNewTemplate({...newTemplate, instruction: e.target.value})} required />
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setEditingSigTemplate(null)} className="flex-1 py-2 border rounded">Back</button>
                            <button type="submit" className="flex-1 py-2 bg-indigo-600 text-white rounded">Save</button>
                        </div>
                    </form>
                )}
            </Modal>
        </fieldset>
    );
}

export default MedicationPanel;